/**
 * /creators/drilldown — per-creator performance drill-down.
 *
 * Declares a TanStack Router route mounted at `/creators/drilldown` that
 * renders the `CreatorDrilldownPage`. Follows the `campaigns.os-targeting`
 * pattern so the root wire-up cycle can pick it up without bespoke plumbing.
 *
 * The page auto-registers via module augmentation in the scaffolded
 * `router.tsx` — the root gate will add an import + route entry there after
 * the prod merge. Agents must not modify `router.tsx` directly.
 */

import { createRoute, Link } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { CreatorDrilldownPage } from "@/features/creator-drilldown/CreatorDrilldownPage";

// ---------------------------------------------------------------------------
// Route — `?creatorId=` deep-link prefill so operators can open the page
// straight from a creator listing. The validator normalizes / drops anything
// unsafe before it reaches the page.
// ---------------------------------------------------------------------------

interface CreatorDrilldownSearch {
	creatorId?: string;
}

export const creatorsDrilldownRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/creators/drilldown",
	validateSearch: (raw: Record<string, unknown>): CreatorDrilldownSearch => {
		const creatorId = raw.creatorId;
		if (typeof creatorId === "string" && creatorId.trim().length > 0) {
			return { creatorId: creatorId.trim() };
		}
		return {};
	},
	component: CreatorsDrilldownRoute,
});

export default creatorsDrilldownRoute;

// ---------------------------------------------------------------------------
// Page shell — breadcrumb + headline + drill-down body. Shell is presentational,
// no emoji, no competitor naming, light theme per brand rules.
// ---------------------------------------------------------------------------

export function CreatorsDrilldownRoute() {
	return (
		<main className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10 sm:py-16">
			<Breadcrumb />
			<CreatorDrilldownPage />
		</main>
	);
}

function Breadcrumb() {
	return (
		<nav aria-label="Breadcrumb" className="text-sm text-zinc-500">
			<ol className="flex flex-wrap items-center gap-1">
				<li>
					<Link
						to="/"
						className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
					>
						Dashboard
					</Link>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li>
					<span className="rounded-[6px] px-1 py-0.5">Creators</span>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li className="text-zinc-900">Drill-down</li>
			</ol>
		</nav>
	);
}
